import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import {
    Search,
    MapPin,
    Eye,
    UserRound,
    X,
    BriefcaseBusiness,
    ArrowUpRight,
    UsersRound,
    Sparkles,
    RefreshCw,
    Mail,
    Phone,
    Clock,
    IndianRupee,
    GraduationCap,
    FileText,
    Hash,
    Building2,
} from "lucide-react";

import { getAllTrainer } from "../services/trainerService";

import "./TalentDirectory.css";


/* =========================================================
   HELPERS
========================================================= */

const EMPTY_ARRAY = [];

const safeString = (value) =>
    value === null || value === undefined ? "" : String(value);

const getInitials = (name) => {
    const clean = safeString(name).trim();

    if (!clean) return "TR";

    return clean
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
};

const normalizeSkills = (skills) => {
    if (Array.isArray(skills)) {
        return skills
            .map((skill) => {
                if (typeof skill === "string") return skill.trim();

                return safeString(
                    skill?.name || skill?.skillName || skill?.skill_name
                ).trim();
            })
            .filter(Boolean);
    }

    if (typeof skills === "string") {
        return skills
            .split(/[,;|]/)
            .map((skill) => skill.trim())
            .filter(Boolean);
    }

    return [];
};

const getRate = (trainer) => {
    const value =
        trainer?.rate ??
        trainer?.commercialRate ??
        trainer?.commercial_rate ??
        trainer?.expectedRate ??
        trainer?.expected_rate ??
        trainer?.price ??
        0;

    const number = Number(String(value).replace(/[^\d.]/g, ""));

    return Number.isFinite(number) ? number : 0;
};

const formatRate = (rate) =>
    new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 2,
    }).format(rate);

const getExperience = (trainer) =>
    safeString(
        trainer?.experience ??
        trainer?.yearsOfExperience ??
        trainer?.years_of_experience ??
        trainer?.exp ??
        ""
    ).trim();

const getLocation = (trainer) => {
    const city = safeString(trainer?.city).trim();
    const state = safeString(trainer?.state).trim();

    if (city && state) return `${city}, ${state}`;

    return city || state || "Location not specified";
};

const getName = (trainer) =>
    safeString(
        trainer?.name ||
        trainer?.fullName ||
        trainer?.full_name ||
        trainer?.trainerName ||
        "Trainer"
    );

const getType = (trainer) =>
    safeString(
        trainer?.professional_type ??
        trainer?.professionalType ??
        trainer?.designation ??
        trainer?.title ??
        ""
    )
        .replace(/[_-]+/g, " ")
        .trim()
        .toLowerCase();

const getEmail = (trainer) =>
    trainer?.email ||
    trainer?.primaryEmail ||
    trainer?.primary_email ||
    trainer?.secondary_email ||
    trainer?.secondaryEmail;

const getPhone = (trainer) =>
    trainer?.phone ||
    trainer?.mobile ||
    trainer?.contactNumber ||
    trainer?.contact_number ||
    trainer?.secondary_phone ||
    trainer?.secondaryPhone;

const getQualification = (trainer) =>
    trainer?.qualification ||
    trainer?.education ||
    trainer?.highestQualification ||
    trainer?.highest_qualification;

const getTrainerList = (response) => {
    const candidates = [
        response,
        response?.data,
        response?.content,
        response?.data?.content,
        response?.data?.data,
        response?.data?.trainers,
        response?.trainers,
    ];

    return candidates.find(Array.isArray) || [];
};

const formatLabel = (key) =>
    String(key)
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (typeof item === "object" && item !== null) {
                    return item.name || item.skillName || "";
                }
                return String(item);
            })
            .filter(Boolean)
            .join(", ");
    }

    if (typeof value === "object") {
        return "";
    }

    return String(value);
};

/* Avoid showing file blobs, internal secrets or duplicate fields
   in the additional-information section. */
const HIDDEN_PROFILE_KEYS = [
    "password",
    "token",
    "secret",
    "resume_data",
    "resumedata",
    "resume_blob",
    "resumecontent",
    "resume_content",
    "resume_file",
    "resumefile",
    "resume_bytes",
    "resumebytes",
    "profile_image_data",
    "image_data",
];

const EXCLUDED_ADDITIONAL_KEYS = [
    "name",
    "fullname",
    "full_name",
    "trainername",
    "email",
    "primaryemail",
    "primary_email",
    "phone",
    "mobile",
    "contactnumber",
    "contact_number",
    "city",
    "state",
    "skills",
    "experience",
    "yearsofexperience",
    "years_of_experience",
    "exp",
    "rate",
    "commercialrate",
    "commercial_rate",
    "expectedrate",
    "expected_rate",
    "price",
    "professionaltype",
    "professional_type",
    "designation",
    "title",
    "qualification",
    "education",
    "highestqualification",
    "highest_qualification",
];

const DetailItem = ({ icon: DetailIcon, label, value }) => (
    <div className="profile-detail-item">
        <span className="profile-detail-label">
            <DetailIcon size={16} />
            {label}
        </span>
        <p>{safeString(value).trim() || "Not provided"}</p>
    </div>
);

/* =========================================================
   TALENT DIRECTORY
========================================================= */

function TalentDirectory({
    title = "Explore Expert Trainers",
    subtitle = "Discover skilled professionals for your training needs.",
    matchTypes = EMPTY_ARRAY,
    icon: Icon = UsersRound,
}) {
    const [talents, setTalents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState("");
    const [location, setLocation] = useState("");
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [planUsage, setPlanUsage] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);
    const [openingTrainerId, setOpeningTrainerId] = useState(null);
    const [downloadingTrainerId, setDownloadingTrainerId] = useState(null);

    const API_BASE = "";

    const getPartnerUser = () => {
        try {
            return JSON.parse(localStorage.getItem("user") || "null");
        } catch {
            return null;
        }
    };

    const normalizeUsageResponse = (responseData) => {
        const data = responseData?.data ?? responseData?.usage ?? responseData ?? {};
        const normalizeCounter = (counter) => {
            const used = Number(counter?.used ?? 0);
            const limit = Number(counter?.limit ?? 0);
            return {
                used: Number.isFinite(used) ? used : 0,
                limit: Number.isFinite(limit) ? limit : 0,
                remaining: Number.isFinite(Number(counter?.remaining))
                    ? Number(counter.remaining)
                    : Math.max(0, limit - used),
            };
        };

        return {
            planName: data?.planName || "Current Plan",
            profileViews: normalizeCounter(data?.profileViews),
            resumeDownloads: normalizeCounter(data?.resumeDownloads),
            jobInvites: normalizeCounter(data?.jobInvites),
            jobPosts: normalizeCounter(data?.jobPosts),
        };
    };

    const refetchUsage = async () => {
        const user = getPartnerUser();
        if (!user?.id) return;

        try {
            setUsageLoading(true);
            const response = await axios.get(`${API_BASE}/api/partner/usage`, {
                params: { partnerUserId: user.id },
            });
            setPlanUsage(normalizeUsageResponse(response.data));
        } catch (err) {
            console.error("Unable to load partner plan usage:", err);
        } finally {
            setUsageLoading(false);
        }
    };

    useEffect(() => {
        refetchUsage();
        // Initial usage fetch only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Fetch trainers */

    const loadTrainers = async () => {
        try {
            setLoading(true);
            setError(false);

            const response = await getAllTrainer();
            setTalents(getTrainerList(response));
        } catch (err) {
            console.error("Unable to load trainer profiles:", err);
            setTalents([]);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const fetchTrainers = async () => {
            try {
                setLoading(true);
                setError(false);

                const response = await getAllTrainer();

                if (isMounted) {
                    setTalents(getTrainerList(response));
                }
            } catch (err) {
                console.error("Unable to load trainer profiles:", err);

                if (isMounted) {
                    setTalents([]);
                    setError(true);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchTrainers();

        return () => {
            isMounted = false;
        };
    }, []);

    /* Close profile modal with Escape */

    useEffect(() => {
        if (!selectedTrainer) return;

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setSelectedTrainer(null);
            }
        };

        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [selectedTrainer]);

    /* Match trainer type */

    const typed = useMemo(() => {
        if (!matchTypes.length) return talents;

        const types = matchTypes.map((type) =>
            safeString(type).trim().toLowerCase()
        );

        return talents.filter((trainer) => {
            const type = getType(trainer);

            return types.some((match) => match && type.includes(match));
        });
    }, [talents, matchTypes]);

    /* Search and location filters */

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        const selectedLocation = location.trim().toLowerCase();

        return typed.filter((trainer) => {
            const name = getName(trainer).toLowerCase();

            const skills = normalizeSkills(trainer?.skills)
                .join(" ")
                .toLowerCase();

            const place = getLocation(trainer).toLowerCase();
            const designation = safeString(
                trainer?.designation || trainer?.title
            ).toLowerCase();

            const type = getType(trainer);

            const matchesSearch =
                !query ||
                name.includes(query) ||
                skills.includes(query) ||
                place.includes(query) ||
                designation.includes(query) ||
                type.includes(query);

            const matchesLocation =
                !selectedLocation || place.includes(selectedLocation);

            return matchesSearch && matchesLocation;
        });
    }, [typed, search, location]);

    /* Clear filters */

    const clearFilters = () => {
        setSearch("");
        setLocation("");
    };

    const hasFilters = Boolean(search.trim() || location.trim());

    /* Resume download and profile-view quota actions */
    const downloadResume = async (trainer) => {
        const trainerId = trainer?.id ?? trainer?.trainerId ?? trainer?.trainer_id;
        if (!trainerId) {
            alert("Trainer ID is missing.");
            return;
        }

        const user = getPartnerUser();
        if (!user?.id) {
            alert("Please log in as a partner.");
            return;
        }

        if (planUsage && planUsage.resumeDownloads.remaining <= 0) {
            alert("Resume download limit reached. Upgrade your plan.");
            return;
        }

        try {
            setDownloadingTrainerId(trainerId);
            const response = await axios.get(
                `${API_BASE}/api/trainers/resume/${trainerId}`,
                {
                    params: { partnerUserId: user.id },
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], {
                type: response.headers["content-type"] || "application/octet-stream",
            });
            if (!blob.size) throw new Error("Resume file is empty.");

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = trainer.resumeName || trainer.resume_name || `${getName(trainer)}-Resume`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);

            // Refresh only after the download endpoint succeeds.
            await refetchUsage();
        } catch (error) {
            let message = "Unable to download resume.";
            if (error.response?.data instanceof Blob) {
                try {
                    const errorData = JSON.parse(await error.response.data.text());
                    message = errorData.message || message;
                } catch {
                    // Use the fallback message.
                }
            } else {
                message = error.response?.data?.message || error.message || message;
            }
            alert(message);
            // Refresh in case backend recorded the action before a network issue.
            await refetchUsage();
        } finally {
            setDownloadingTrainerId(null);
        }
    };

   

const openProfile = async (trainer) => {
    window.alert("openProfile function is running");
    const user = getPartnerUser();
    const trainerId = trainer?.id ?? trainer?.trainerId ?? trainer?.trainer_id;

    if (!user?.id) {
        alert("Please log in as a partner.");
        return;
    }

    if (!trainerId) {
        alert("Trainer ID is missing.");
        return;
    }

    try {
        setOpeningTrainerId(trainerId);

        const response = await axios.post(
            `${API_BASE}/api/trainers/${trainerId}/view`,
            null,
            {
                params: { partnerUserId: user.id },
            }
        );

        console.log("Profile view recorded:", response.data);

        setSelectedTrainer(trainer);

        // Update the usage counter in this component.
        await refetchUsage();

        // Notify other components, such as the dashboard.
        window.dispatchEvent(
            new CustomEvent("partner-usage-updated")
        );
    } catch (error) {
        console.error("Profile view failed:", error);

        const message =
            error.response?.data?.message ||
            "Unable to open this trainer profile.";

        alert(message);
    } finally {
        setOpeningTrainerId(null);
    }
};



    const closeProfile = () => {
        setSelectedTrainer(null);
    };

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <main className="talent-page">

            {/* HERO */}

            <section className="talent-hero">
                <div className="talent-hero-inner">
                    <div className="talent-hero-icon">
                        <Icon size={27} strokeWidth={1.8} />
                    </div>

                    <div className="talent-hero-content">
                        <span className="talent-eyebrow">
                            <Sparkles size={13} />
                            CERTED TALENT NETWORK
                        </span>

                        <h1>{title}</h1>
                        <p>{subtitle}</p>
                    </div>

                    <div className="talent-count">
                        <div className="talent-count-icon">
                            <UsersRound size={19} />
                        </div>

                        <div>
                            <strong>
                                {loading ? "—" : filtered.length}
                            </strong>
                            <span>
                                {filtered.length === 1
                                    ? "Profile found"
                                    : "Profiles found"}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* DIRECTORY CONTENT */}

            <section className="talent-shell">
                <div className="talent-section-heading">
                    <div>
                        <span className="talent-section-label">
                            FIND YOUR EXPERT
                        </span>
                        <h2>Discover the Right Talent</h2>
                        <p>
                            Search trainer profiles by skills, expertise or
                            location.
                        </p>
                    </div>
                </div>

                {planUsage && (
                    <div className="talent-usage-summary" aria-live="polite">
                        <span><Eye size={14} /> Profile views: <strong>{planUsage.profileViews.used}/{planUsage.profileViews.limit}</strong> · {planUsage.profileViews.remaining} remaining</span>
                        <span><FileText size={14} /> Resume downloads: <strong>{planUsage.resumeDownloads.used}/{planUsage.resumeDownloads.limit}</strong> · {planUsage.resumeDownloads.remaining} remaining</span>
                        {usageLoading && <span className="talent-usage-refreshing">Updating usage…</span>}
                    </div>
                )}

                {/* SEARCH */}

                <div className="talent-toolbar">
                    <div className="talent-input talent-search-input">
                        <Search size={19} />

                        <input
                            type="search"
                            aria-label="Search trainers"
                            placeholder="Search name, skills or expertise..."
                            value={search}
                            onChange={(event) =>
                                setSearch(event.target.value)
                            }
                        />

                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch("")}
                                aria-label="Clear search"
                                title="Clear search"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    <div className="talent-input talent-location-input">
                        <MapPin size={19} />

                        <input
                            type="text"
                            aria-label="Filter by city or state"
                            placeholder="City or state"
                            value={location}
                            onChange={(event) =>
                                setLocation(event.target.value)
                            }
                        />

                        {location && (
                            <button
                                type="button"
                                onClick={() => setLocation("")}
                                aria-label="Clear location"
                                title="Clear location"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {hasFilters && (
                        <button
                            type="button"
                            className="talent-clear-button"
                            onClick={clearFilters}
                        >
                            <RefreshCw size={15} />
                            Clear filters
                        </button>
                    )}
                </div>

                {/* RESULTS SUMMARY */}

                {!loading && !error && (
                    <div className="talent-results-bar">
                        <p>
                            Showing <strong>{filtered.length}</strong>{" "}
                            {filtered.length === 1
                                ? "professional"
                                : "professionals"}
                        </p>

                        {hasFilters && (
                            <span className="talent-filter-active">
                                Filters applied
                            </span>
                        )}
                    </div>
                )}

                {/* LOADING */}

                {loading ? (
                    <div
                        className="talent-grid"
                        aria-label="Loading trainer profiles"
                        aria-busy="true"
                    >
                        {Array.from({ length: 8 }).map((_, index) => (
                            <div className="talent-skeleton" key={index}>
                                <div className="skeleton-head">
                                    <div className="skeleton-avatar" />
                                    <div className="skeleton-lines">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                </div>

                                <div className="skeleton-skills">
                                    <span />
                                    <span />
                                    <span />
                                </div>

                                <div className="skeleton-footer">
                                    <span />
                                    <span />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    /* ERROR */

                    <div className="talent-empty">
                        <div className="talent-empty-icon">
                            <RefreshCw size={30} />
                        </div>

                        <h3>Unable to load profiles</h3>
                        <p>
                            We could not retrieve trainer information.
                            Please try again.
                        </p>

                        <button
                            type="button"
                            className="talent-retry-button"
                            onClick={loadTrainers}
                        >
                            <RefreshCw size={16} />
                            Try again
                        </button>
                    </div>
                ) : filtered.length === 0 ? (
                    /* EMPTY STATE */

                    <div className="talent-empty">
                        <div className="talent-empty-icon">
                            <UserRound size={32} />
                        </div>

                        <h3>No profiles found</h3>
                        <p>
                            Try a different name, skill or location to
                            discover more professionals.
                        </p>

                        {hasFilters && (
                            <button
                                type="button"
                                className="talent-retry-button"
                                onClick={clearFilters}
                            >
                                <X size={16} />
                                Clear filters
                            </button>
                        )}
                    </div>
                ) : (
                    /* TRAINER CARDS */

                    <div className="talent-grid">
                        {filtered.map((trainer, index) => {
                            const name = getName(trainer).trim();
                            const skills = normalizeSkills(trainer?.skills);
                            const experience = getExperience(trainer);
                            const rate = getRate(trainer);
                            const place = getLocation(trainer);

                            const designation = safeString(
                                trainer?.designation ||
                                trainer?.title ||
                                trainer?.professional_type ||
                                trainer?.professionalType ||
                                "Training Professional"
                            )
                                .replace(/[_-]+/g, " ")
                                .trim();

                            return (
                                <article
                                    className="talent-card"
                                    key={trainer?.id ?? `${name}-${index}`}
                                >
                                    <span className="talent-card-number">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>

                                    {/* CARD HEADER */}

                                    <div className="talent-card-head">
                                        <div
                                            className="talent-avatar"
                                            aria-hidden="true"
                                        >
                                            {getInitials(name)}
                                        </div>

                                        <div className="talent-card-info">
                                            <h3 title={name}>{name}</h3>

                                            <p
                                                className="talent-designation"
                                                title={designation}
                                            >
                                                <BriefcaseBusiness size={13} />
                                                <span>{designation}</span>
                                            </p>

                                            <p className="talent-location">
                                                <MapPin size={13} />
                                                <span title={place}>{place}</span>
                                            </p>
                                        </div>
                                    </div>

                                    {/* EXPERIENCE */}

                                    {experience && (
                                        <div className="talent-experience">
                                            <span className="talent-experience-dot" />
                                            <span>Experience</span>
                                            <strong>{experience}</strong>
                                        </div>
                                    )}

                                    {/* SKILLS */}

                                    <div className="talent-skills">
                                        {skills.length > 0 ? (
                                            <>
                                                {skills.slice(0, 4).map(
                                                    (skill, skillIndex) => (
                                                        <span
                                                            key={`${skill}-${skillIndex}`}
                                                        >
                                                            {skill}
                                                        </span>
                                                    )
                                                )}

                                                {skills.length > 4 && (
                                                    <span className="talent-more-skills">
                                                        +{skills.length - 4} more
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            <span className="talent-no-skills">
                                                Skills not listed
                                            </span>
                                        )}
                                    </div>

                                    {/* CARD FOOTER */}

                                    <div className="talent-card-foot">
                                        <div className="talent-rate">
                                            <small>Training Rate</small>

                                            <strong>
                                                {rate > 0
                                                    ? `₹${formatRate(rate)}`
                                                    : "On request"}
                                            </strong>

                                            {rate > 0 && (
                                                <span className="talent-rate-unit">
                                                    / day
                                                </span>
                                            )}
                                        </div>

                                        {/* VIEW PROFILE */}

                                        
<button
    type="button"
    className="talent-view-profile-button"
    onClick={() => openProfile(trainer)}
    disabled={
        openingTrainerId ===
        (trainer?.id ?? trainer?.trainerId ?? trainer?.trainer_id)
    }
>
    <Eye size={16} />
    <span>
        {openingTrainerId ===
        (trainer?.id ?? trainer?.trainerId ?? trainer?.trainer_id)
            ? "Opening..."
            : "View Profile"}
    </span>
    <ArrowUpRight size={14} />
</button>

                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* =================================================
                TRAINER PROFILE MODAL
            ================================================= */}

            {selectedTrainer && (
                <div
                    className="profile-modal-overlay"
                    onClick={closeProfile}
                >
                    <section
                        className="profile-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="profile-modal-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {/* MODAL HEADER */}

                        <header className="profile-modal-header">
                            <div>
                                <span className="profile-modal-eyebrow">
                                    CERTED TALENT NETWORK
                                </span>
                                <h2 id="profile-modal-title">
                                    Trainer Profile
                                </h2>
                            </div>

                            <button
                                type="button"
                                className="profile-modal-close"
                                onClick={closeProfile}
                                aria-label="Close profile"
                                title="Close profile"
                            >
                                <X size={22} />
                            </button>
                        </header>

                        <div className="profile-modal-body">
                            {/* PROFILE SUMMARY */}

                            <div className="profile-hero">
                                <div className="profile-avatar">
                                    {getInitials(getName(selectedTrainer))}
                                </div>

                                <div className="profile-hero-info">
                                    <h3>{getName(selectedTrainer)}</h3>

                                    <p>
                                        {safeString(
                                            selectedTrainer.designation ||
                                            selectedTrainer.title ||
                                            selectedTrainer.professional_type ||
                                            selectedTrainer.professionalType ||
                                            "Training Professional"
                                        )}
                                    </p>

                                    <span className="profile-location">
                                        <MapPin size={15} />
                                        {getLocation(selectedTrainer)}
                                    </span>
                                </div>
                            </div>

                            {/* PERSONAL & CONTACT DETAILS */}

                            <div className="profile-section">
                                <h4>
                                    <UserRound size={18} />
                                    Personal & Contact Details
                                </h4>

                                <div className="profile-details-grid">
                                    <DetailItem
                                        icon={Hash}
                                        label="Trainer ID"
                                        value={selectedTrainer.id}
                                    />

                                    <DetailItem
                                        icon={Mail}
                                        label="Email"
                                        value={getEmail(selectedTrainer)}
                                    />

                                    <DetailItem
                                        icon={Phone}
                                        label="Phone"
                                        value={getPhone(selectedTrainer)}
                                    />

                                    <DetailItem
                                        icon={Clock}
                                        label="Age"
                                        value={selectedTrainer.age}
                                    />

                                    <DetailItem
                                        icon={MapPin}
                                        label="City"
                                        value={selectedTrainer.city}
                                    />

                                    <DetailItem
                                        icon={MapPin}
                                        label="State"
                                        value={selectedTrainer.state}
                                    />

                                    <DetailItem
                                        icon={Hash}
                                        label="Pincode"
                                        value={selectedTrainer.pincode}
                                    />

                                    <DetailItem
                                        icon={GraduationCap}
                                        label="Qualification"
                                        value={getQualification(selectedTrainer)}
                                    />
                                </div>
                            </div>

                            {/* PROFESSIONAL DETAILS */}

                            <div className="profile-section">
                                <h4>
                                    <BriefcaseBusiness size={18} />
                                    Professional Details
                                </h4>

                                <div className="profile-details-grid">
                                    <DetailItem
                                        icon={BriefcaseBusiness}
                                        label="Professional Type"
                                        value={
                                            selectedTrainer.professional_type ||
                                            selectedTrainer.professionalType ||
                                            selectedTrainer.designation
                                        }
                                    />

                                    <DetailItem
                                        icon={Clock}
                                        label="Experience"
                                        value={getExperience(selectedTrainer)}
                                    />

                                    <DetailItem
                                        icon={IndianRupee}
                                        label="Training Rate"
                                        value={
                                            getRate(selectedTrainer) > 0
                                                ? `₹${formatRate(getRate(selectedTrainer))} / day`
                                                : ""
                                        }
                                    />

                                    <DetailItem
                                        icon={Building2}
                                        label="Training Mode"
                                        value={
                                            selectedTrainer.training_mode ||
                                            selectedTrainer.trainingMode
                                        }
                                    />

                                    <DetailItem
                                        icon={GraduationCap}
                                        label="Education"
                                        value={selectedTrainer.education}
                                    />

                                    <DetailItem
                                        icon={FileText}
                                        label="Certifications"
                                        value={
                                            selectedTrainer.certifications ||
                                            selectedTrainer.certification
                                        }
                                    />

                                    <DetailItem
                                        icon={FileText}
                                        label="Languages"
                                        value={
                                            selectedTrainer.languages ||
                                            selectedTrainer.language
                                        }
                                    />

                                    <DetailItem
                                        icon={Hash}
                                        label="Profile Status"
                                        value={selectedTrainer.status}
                                    />
                                </div>
                            </div>

                            {/* ALL SKILLS */}

                            <div className="profile-section">
                                <h4>
                                    <Sparkles size={18} />
                                    Skills & Expertise
                                </h4>

                                <div className="profile-skills">
                                    {normalizeSkills(selectedTrainer.skills).length > 0 ? (
                                        normalizeSkills(selectedTrainer.skills).map(
                                            (skill, index) => (
                                                <span key={`${skill}-${index}`}>
                                                    {skill}
                                                </span>
                                            )
                                        )
                                    ) : (
                                        <p className="profile-empty">
                                            No skills available.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* OTHER FIELDS RETURNED BY THE API */}

                            {(() => {
                                const entries = Object.entries(selectedTrainer)
                                    .filter(([key, value]) => {
                                        const normalizedKey = key
                                            .replace(/[^a-zA-Z0-9]/g, "")
                                            .toLowerCase();

                                        if (
                                            HIDDEN_PROFILE_KEYS.some((hidden) =>
                                                normalizedKey.includes(
                                                    hidden.replace(/[^a-zA-Z0-9]/g, "")
                                                )
                                            )
                                        ) {
                                            return false;
                                        }

                                        if (
                                            EXCLUDED_ADDITIONAL_KEYS.includes(
                                                key.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()
                                            )
                                        ) {
                                            return false;
                                        }

                                        if (
                                            key.toLowerCase().includes("resume") ||
                                            key.toLowerCase().includes("password") ||
                                            key.toLowerCase().includes("token")
                                        ) {
                                            return false;
                                        }

                                        return (
                                            formatFieldValue(value).trim() !== ""
                                        );
                                    });

                                if (entries.length === 0) return null;

                                return (
                                    <div className="profile-section">
                                        <h4>
                                            <FileText size={18} />
                                            Additional Information
                                        </h4>

                                        <div className="profile-details-grid">
                                            {entries.map(([key, value]) => (
                                                <DetailItem
                                                    key={key}
                                                    icon={FileText}
                                                    label={formatLabel(key)}
                                                    value={formatFieldValue(value)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* MODAL FOOTER */}

                        {/* MODAL FOOTER */}

<footer className="profile-modal-footer">
    <button
        type="button"
        className="profile-download-btn"
        onClick={() => downloadResume(selectedTrainer)}
        disabled={downloadingTrainerId === (selectedTrainer?.id ?? selectedTrainer?.trainerId ?? selectedTrainer?.trainer_id) || (planUsage !== null && planUsage.resumeDownloads.remaining <= 0)}
        title={planUsage?.resumeDownloads.remaining <= 0 ? "Limit reached - upgrade plan" : `Resume downloads remaining: ${planUsage?.resumeDownloads.remaining ?? "…"}`}
    >
        <FileText size={18} />
        {downloadingTrainerId ? "Downloading…" : "Download Resume"}
        {planUsage && <span className="talent-usage-badge">{planUsage.resumeDownloads.remaining} left</span>}
    </button>

    <button
        type="button"
        className="profile-close-btn"
        onClick={closeProfile}
    >
        Close Profile
    </button>
</footer>
                    </section>
                </div>
            )}
        </main>
    );
}

export default TalentDirectory;
